// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useAuth, useUser } from '@/auth/clerkShim';
import _ from 'lodash';
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import useRegistrationStore from '@store/registrationStore';
import useOrganizationStore from '@store/organizationStore';
import { CONFIG_DEFAULTS } from '@staty/shared/utils/configDefaults';
import useAdminStore from '@store/adminStore';
import usePaymentProductStore from '@store/paymentProductStore';
import EmailInput from '@/components/EmailInput/EmailInput';
import { axios } from '@store/axios';
import StripeConnectStatusCard from '@/components/StripeConnectStatus/StripeConnectStatusCard';
import CustomDomainCard from '@/components/CustomDomainCard/CustomDomainCard';
import useStripeConnectStatus from '@/hooks/useStripeConnectStatus';
import StripeSetupModal from '@/components/StripeSetupModal';
import BrowserPreview from '@components/BrowserPreview';
import ProfileFieldsEditor from './ProfileFieldsEditor';
import { SPORT_NAMES } from '@staty/shared/banners';
import PublicUrlField from '@/components/Settings/PublicUrlField';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { ListSkeleton } from '@/components/Page/PageSkeletons';

// Icons
import { 
  Settings, 
  CreditCard, 
  Users, 
  Shield, 
  Key,
  Globe, 
  Image, 
  Bell,
  Mail,
  Calendar,
  FileText,
  Database,
  Zap,
  Eye,
  EyeOff,
  Save,
  Upload,
  Trash2,
  Plus,
  Copy,
  CheckCircle,
  AlertCircle,
  Info,
  Trophy,
  UserCircle,
  GripVertical,
  Settings as SettingsIcon
} from 'lucide-react';

const businessEntityOptions = [
  { value: "individual", label: "Individual" },
  { value: "llc", label: "LLC" },
  { value: "corporation", label: "Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "nonprofit", label: "Non-Profit" },
];

const sportsOptions = [
  { value: "basketball", label: "Basketball" },
  { value: "baseball", label: "Baseball" },
  { value: "football", label: "Football" },
  { value: "soccer", label: "Soccer" },
  { value: "volleyball", label: "Volleyball" },
  { value: "pickleball", label: "Pickleball" },
  { value: "tennis", label: "Tennis" },
  { value: "golf", label: "Golf" },
  { value: "track", label: "Track" },
  { value: "judo", label: "Judo" },
  { value: "hockey", label: "Hockey" },
  { value: "softball", label: "Softball" },
  { value: "other", label: "Other" },
];

const baseUrl = `${process.env.API_URL}/api/v1`;

function SettingAdmin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { leagueName, league } = useOutletContext() as any;
  const [activeTab, setActiveTab] = useState("general");
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  // API keys are PARKED (2026-08 tranche 2): the tab trigger was already
  // hidden; the dead TabsContent + its loaders/handlers were removed. The
  // backend 501 stubs remain — reintroduce the UI when keys actually ship.

  // Form states
  const [selectedBusinessEntity, setSelectedBusinessEntity] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [leagueSettings, setLeagueSettings] = useState({
    displayName: league?.displayName || "",
    about: league?.about || "",
    location: league?.location || "",
    emails: league?.emails || [],
    hasBanner: league?.hasBanner || false,
    hasRegistration: league?.hasRegistration || true,
    profileFields: league?.settings?.profileFields || [],
    duprEnabled: !!league?.settings?.duprEnabled,
  });

  // Store original state for comparison
  const [originalLeagueSettings, setOriginalLeagueSettings] = useState({
    displayName: league?.displayName || "",
    about: league?.about || "",
    location: league?.location || "",
    emails: league?.emails || [],
    hasBanner: league?.hasBanner || false,
    hasRegistration: league?.hasRegistration || true,
    profileFields: league?.settings?.profileFields || [],
    duprEnabled: !!league?.settings?.duprEnabled,
  });


  const [registrationSettings, setRegistrationSettings] = useState({
    emailReminderTeam: true,
    numDaysToRemindTeam: 7,
    rosterLock: false,
    rosterLockDate: null,
  });

  const [teamSettings, setTeamSettings] = useState({
    requirePlayers: CONFIG_DEFAULTS.requirePlayers,
    requireCoaches: CONFIG_DEFAULTS.requireCoaches,
    minPlayers: 4, // FORM SEED, not a platform default (ADR 0035)
    maxPlayers: 11, // FORM SEED, not a platform default (ADR 0035)
    minCoaches: CONFIG_DEFAULTS.minCoaches,
    maxCoaches: CONFIG_DEFAULTS.maxCoaches,
    hideTeams: CONFIG_DEFAULTS.hideTeams,
    disableSync: CONFIG_DEFAULTS.disableSync,
  });

  const [tiebreakerSettings, setTiebreakerSettings] = useState({
    basketball: ['headToHead', 'pointsScored', 'pointDifferential'],
    football: ['headToHead', 'division', 'conference'],
    baseball: ['headToHead', 'division', 'last-half'],
    soccer: ['headToHead', 'goal-diff', 'goals-for'],
  });

  // Store hooks
  const { createStripeConnectLink, updateOrganization, getStripeDashboardLink } = useOrganizationStore((state: any) => ({
    createStripeConnectLink: state.createStripeConnectLink(getToken),
    getStripeDashboardLink: state.getStripeDashboardLink(getToken),
    updateOrganization: state.updateOrganization(getToken),
  }));

  const { admin, admins, getAdminsByLeagueName } = useAdminStore((state: any) => ({
    admin: state.admin,
    admins: state.admins,
    getAdminsByLeagueName: state.getAdminsByLeagueName(getToken),
  }));

  const { paymentProducts, getPaymentProductsByLeagueName } = usePaymentProductStore((state: any) => ({
    paymentProducts: state.paymentProducts,
    getPaymentProductsByLeagueName: state.getPaymentProductsByLeagueName(getToken),
  }));

  // Effects
  useEffect(() => {
    if (user) {
      setBusinessEmail(user.primaryEmailAddress.emailAddress);
    }
  }, [user]);

  useEffect(() => {
    getAdminsByLeagueName(leagueName);
    getPaymentProductsByLeagueName(leagueName);
  }, [leagueName]);

  // Handlers
  const stripeReturnUrl = `${window.location.origin}/admin/settings`;
  const openStripeLink = (data) => {
    // The store toasts server errors itself; data is undefined/`{error}` then.
    if (data?.accountLink) window.open(data.accountLink, '_blank');
  };
  // Single-use Express-dashboard login link (payouts, balances, settings live
  // there — that's why there's no separate "payout reports" button).
  const openStripeDashboard = async () => {
    const result = await getStripeDashboardLink(league?._id);
    if (result?.url) window.open(result.url, '_blank');
  };

  const handleSaveLeagueSettings = async () => {
    try {
      const token = await getToken();
      const { profileFields, duprEnabled, ...flat } = leagueSettings;
      const saved = await updateOrganization({
        _id: league._id,
        ...flat,
        settings: {
          ...(league?.settings || {}),
          profileFields,
          duprEnabled,
        },
      });
      // The store swallows axios errors and returns undefined (INT-0040):
      // treat that as a failure so the error toast below actually fires.
      if (!saved || saved.status === 0) throw new Error(saved?.error || 'Save failed');

      // Update original state to reflect the saved changes
      setOriginalLeagueSettings({...leagueSettings});
      
      // Show success toast
      toast.success("League settings saved successfully!");
    } catch (error) {
      console.error('Error saving league settings:', error);
      // Show error toast
      toast.error("Failed to save league settings. Please try again.");
    }
  };

  const handleSaveRegistrationSettings = async () => {
    // Save registration settings
    console.log('Saving registration settings:', registrationSettings);
  };

  const handleSaveTeamSettings = async () => {
    // Save team settings
    console.log('Saving team settings:', teamSettings);
  };

  const handleSaveTiebreakerSettings = async () => {
    // Save tiebreaker settings
    console.log('Saving tiebreaker settings:', tiebreakerSettings);
  };

  // Live Connect status (ADR 0040): account-exists vs charges-enabled are
  // different states — the badges below show all three.
  const { status: stripeStatus, loading: stripeStatusLoading, refetch: refetchStripeStatus } = useStripeConnectStatus(league?._id);
  const stripeHasAccount = !!(stripeStatus?.hasAccount || league?.stepsTaken?.createdStripeAccount);
  const stripeReady = !!stripeStatus?.chargesEnabled
    || (stripeStatus?.error === 'unavailable' && stripeHasAccount);

  // Check if there are any changes
  const hasChanges = !_.isEqual(leagueSettings, originalLeagueSettings);

  return (
    // fullWidth: rail-mode page (2026-09-11) — use the width like Appearance.
    <PageContainer fullWidth>
        {/* Header */}
        <PageHeader
          title={(
            <span className="flex items-center space-x-2">
              <SettingsIcon className="w-5 h-5 text-primary" />
              <span>Organization Settings</span>
            </span>
          )}
          description={<>Configure your {leagueName} organization preferences and integrations</>}
          actions={(
            <>
              <Button variant="outline" size="lg" onClick={() => navigate('/admin')}>
                Back to Dashboard
              </Button>
              <Button size="lg" onClick={handleSaveLeagueSettings} disabled={!hasChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save All Changes
              </Button>
            </>
          )}
        />

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 bg-muted shadow-sm">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="registration" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Registration
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <UserCircle className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Teams
            </TabsTrigger>
            <TabsTrigger value="admins" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Admins
            </TabsTrigger>
            {/* Developer API Keys tab hidden — deferred during the Clerk migration
                (keys were Clerk-backed). Re-enable with a Mongo key store. */}
          </TabsList>

          {/* General Settings Tab */}
          <TabsContent value="general" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* League Information */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    League Information
                  </CardTitle>
                  <CardDescription>Basic information about your league</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Sport is single-sourced from organization.sports[0],
                      chosen once at org creation (ADR 0010) — display only,
                      never an editor (owner request 2026-09-29). */}
                  <div className="space-y-2">
                    <Label>Sport</Label>
                    <div
                      className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"
                      data-testid="settings-sport"
                    >
                      <span className="font-medium capitalize">
                        {SPORT_NAMES[league?.sports?.[0]] || league?.sports?.[0] || '—'}
                      </span>
                      <span className="text-xs text-muted-foreground">Set at organization creation</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Display Name</Label>
                    <Input
                      id="displayName"
                      value={leagueSettings.displayName}
                      onChange={(e) => setLeagueSettings({...leagueSettings, displayName: e.target.value})}
                      placeholder="Enter league display name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={leagueSettings.location}
                      onChange={(e) => setLeagueSettings({...leagueSettings, location: e.target.value})}
                      placeholder="Enter league location"
                    />
                  </div>
                  {/* INT-0040: the tenant subdomain is changeable here. */}
                  <PublicUrlField org={league} />
                  <div className="space-y-2">
                    <Label htmlFor="about">About</Label>
                    <Textarea
                      id="about"
                      value={leagueSettings.about}
                      onChange={(e) => setLeagueSettings({...leagueSettings, about: e.target.value})}
                      placeholder="Describe your league"
                      rows={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emails">Contact Emails</Label>
                    <EmailInput
                      emailList={leagueSettings.emails}
                      onChange={(emails) => setLeagueSettings({...leagueSettings, emails})}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* No payments card here: payout status + setup live ONLY on
                  the Payments tab (owner-reported 2026-10-18: the General tab
                  showed a duplicate "Payments" card). Keep the General tab
                  free of Stripe UI. */}
              <div className="space-y-6">
                {/* League Features */}
                <Card className="shadow-sm border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      League Features
                    </CardTitle>
                    <CardDescription>Enable or disable league features</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Registration System</p>
                        <p className="text-xs text-muted-foreground">Allow teams to register online</p>
                      </div>
                      <Switch
                        checked={leagueSettings.hasRegistration}
                        onCheckedChange={(checked) => setLeagueSettings({...leagueSettings, hasRegistration: checked})}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Custom domain (INT-0007) — dedicated endpoints, own save flow */}
                <CustomDomainCard organizationId={league?._id} />
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payout account */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Payout account
                  </CardTitle>
                  <CardDescription>Where registration revenue is deposited</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <StripeConnectStatusCard
                    status={stripeStatus}
                    loading={stripeStatusLoading}
                    onConnect={() => setIsStripeModalOpen(true)}
                    notConnectedHint="Set up payments to collect registration fees and donations."
                  />
                  {stripeReady && (
                    <Button variant="outline" className="w-full" onClick={openStripeDashboard}>
                      <Globe className="h-4 w-4 mr-2" />
                      Manage payout account
                    </Button>
                  )}
                  <p className="text-center text-xs text-muted-foreground">
                    Payments securely processed by Stripe.
                  </p>
                </CardContent>
              </Card>

              {/* Payment Products */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Payment Products
                  </CardTitle>
                  <CardDescription>Manage your payment products and pricing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {paymentProducts.length > 0 ? (
                    <div className="space-y-2">
                      {paymentProducts.slice(0, 3).map((product) => (
                        <div key={product._id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div>
                            <p className="text-sm font-medium">{product.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {product.isFree ? 'Free' : `$${(product.amount / 100).toFixed(2)}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {product.earlyBirdAmount != null && product.earlyBirdDeadline && (
                              <Badge variant="outline" className="text-xs">Early-bird</Badge>
                            )}
                            {product.additionalTeamAmount != null && (
                              <Badge variant="outline" className="text-xs">Multi-team</Badge>
                            )}
                            <Badge variant={product.active ? "default" : "secondary"}>
                              {product.active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-muted-foreground mb-2">No payment products configured</p>
                    </div>
                  )}
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/admin/registration')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Manage Pricing in Registrations
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Registration Settings Tab */}
          <TabsContent value="registration" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Email Notifications */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Email Notifications
                  </CardTitle>
                  <CardDescription>Configure email reminders and notifications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Team Registration Reminders</p>
                      <p className="text-xs text-muted-foreground">Send reminder emails to teams</p>
                    </div>
                    <Switch
                      checked={registrationSettings.emailReminderTeam}
                      onCheckedChange={(checked) => setRegistrationSettings({...registrationSettings, emailReminderTeam: checked})}
                    />
                  </div>
                  {registrationSettings.emailReminderTeam && (
                    <div className="space-y-2">
                      <Label htmlFor="reminder-days">Days Before Deadline</Label>
                      <Input
                        id="reminder-days"
                        type="number"
                        value={registrationSettings.numDaysToRemindTeam}
                        onChange={(e) => setRegistrationSettings({...registrationSettings, numDaysToRemindTeam: parseInt(e.target.value)})}
                        min="1"
                        max="30"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Registration Deadlines */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Registration Deadlines
                  </CardTitle>
                  <CardDescription>Set important registration dates</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Roster Lock</p>
                      <p className="text-xs text-muted-foreground">Lock team rosters after deadline</p>
                    </div>
                    <Switch
                      checked={registrationSettings.rosterLock}
                      onCheckedChange={(checked) => setRegistrationSettings({...registrationSettings, rosterLock: checked})}
                    />
                  </div>
                  {registrationSettings.rosterLock && (
                    <div className="space-y-2">
                      <Label htmlFor="roster-lock-date">Roster Lock Date</Label>
                      <Input
                        id="roster-lock-date"
                        type="datetime-local"
                        value={registrationSettings.rosterLockDate || ''}
                        onChange={(e) => setRegistrationSettings({...registrationSettings, rosterLockDate: e.target.value})}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab — Create Profile field editor */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Configure — all business logic lives inside the editor */}
              <ProfileFieldsEditor
                profileFields={leagueSettings.profileFields || []}
                orgSport={_.get(league, 'sports[0]')}
                onChange={(nextFields) => setLeagueSettings({ ...leagueSettings, profileFields: nextFields })}
              />

              {/* Right: Live Preview */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Live Preview
                  </CardTitle>
                  <CardDescription>
                    How the Create Profile form will look to users on the registration site.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <BrowserPreview
                    fields={leagueSettings.profileFields || []}
                    onRemoveField={(idx: number) => {
                      const next = [...(leagueSettings.profileFields || [])];
                      next.splice(idx, 1);
                      setLeagueSettings({ ...leagueSettings, profileFields: next });
                    }}
                    onReorderFields={(nextFields: any[]) =>
                      setLeagueSettings({ ...leagueSettings, profileFields: nextFields })
                    }
                    url="yourleague.com/create-profile"
                    title="Create Profile"
                    addButtonLabel=""
                    emptyMessage="No profile fields yet. Add fields on the left to see them here."
                    type="team"
                    variant="create-profile"
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Team Requirements */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Team Requirements
                  </CardTitle>
                  <CardDescription>Configure team composition rules</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="min-players">Min Players</Label>
                      <Input
                        id="min-players"
                        type="number"
                        value={teamSettings.minPlayers}
                        onChange={(e) => setTeamSettings({...teamSettings, minPlayers: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-players">Max Players</Label>
                      <Input
                        id="max-players"
                        type="number"
                        value={teamSettings.maxPlayers}
                        onChange={(e) => setTeamSettings({...teamSettings, maxPlayers: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="min-coaches">Min Coaches</Label>
                      <Input
                        id="min-coaches"
                        type="number"
                        value={teamSettings.minCoaches}
                        onChange={(e) => setTeamSettings({...teamSettings, minCoaches: parseInt(e.target.value)})}
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-coaches">Max Coaches</Label>
                      <Input
                        id="max-coaches"
                        type="number"
                        value={teamSettings.maxCoaches}
                        onChange={(e) => setTeamSettings({...teamSettings, maxCoaches: parseInt(e.target.value)})}
                        min="0"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Team Settings */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Team Settings
                  </CardTitle>
                  <CardDescription>Additional team configuration options</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Require Players</p>
                      <p className="text-xs text-muted-foreground">Teams must have players</p>
                    </div>
                    <Switch
                      checked={teamSettings.requirePlayers}
                      onCheckedChange={(checked) => setTeamSettings({...teamSettings, requirePlayers: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Require Coaches</p>
                      <p className="text-xs text-muted-foreground">Teams must have coaches</p>
                    </div>
                    <Switch
                      checked={teamSettings.requireCoaches}
                      onCheckedChange={(checked) => setTeamSettings({...teamSettings, requireCoaches: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Hide Teams</p>
                      <p className="text-xs text-muted-foreground">Hide teams from public view</p>
                    </div>
                    <Switch
                      checked={teamSettings.hideTeams}
                      onCheckedChange={(checked) => setTeamSettings({...teamSettings, hideTeams: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Disable Sync</p>
                      <p className="text-xs text-muted-foreground">Disable automatic data sync</p>
                    </div>
                    <Switch
                      checked={teamSettings.disableSync}
                      onCheckedChange={(checked) => setTeamSettings({...teamSettings, disableSync: checked})}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tiebreaker Settings */}
            <Card className="shadow-sm border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Tiebreaker Settings
                </CardTitle>
                <CardDescription>Configure tiebreaker rules for different sports</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(tiebreakerSettings).map(([sport, tiebreakers]) => (
                  <div key={sport} className="space-y-3">
                    <h4 className="font-medium capitalize">{sport} Tiebreakers</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {tiebreakers.map((tiebreaker, index) => (
                        <Badge key={tiebreaker} variant="outline" className="text-xs">
                          {index + 1}. {tiebreaker}
                        </Badge>
                      ))}
                    </div>
                    <Separator />
                  </div>
                ))}
              </CardContent>
            </Card>
            {/* Display Settings (moved from the removed Appearance tab, INT-0009) */}
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Display Settings
                  </CardTitle>
                  <CardDescription>Configure how your league appears</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Hide Teams</p>
                      <p className="text-xs text-muted-foreground">Hide teams from public view</p>
                    </div>
                    <Switch
                      checked={teamSettings.hideTeams}
                      onCheckedChange={(checked) => setTeamSettings({...teamSettings, hideTeams: checked})}
                    />
                  </div>
                </CardContent>
              </Card>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins" className="space-y-6">
            <Card className="shadow-sm border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Admin Management
                </CardTitle>
                <CardDescription>Manage league administrators and permissions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {admins.length > 0 ? (
                  <div className="space-y-3">
                    {admins.map((admin) => (
                      <div key={admin._id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{admin.firstName} {admin.lastName}</p>
                          <p className="text-xs text-muted-foreground">{admin.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">Admin</Badge>
                          <Button variant="outline" size="sm">
                            <Settings className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-2">No additional admins found</p>
                  </div>
                )}
                <Button variant="outline" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Admin
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* The Appearance tab moved to its own page (INT-0009):
              /admin/appearance under System & Tools — template, hero,
              palette, font, logo, and the banner editor live there. The
              hide-flag toggles stay in Settings (Teams tab). */}

        </Tabs>

        {/* Shared embedded payment-setup dialog (ADR 0044/0051) — Settings
            previously used the legacy hosted-link flow; it now opens the same
            in-page setup as Get Started and the registration Payments pane. */}
        <StripeSetupModal
          open={isStripeModalOpen}
          onOpenChange={setIsStripeModalOpen}
          orgId={league?._id}
          onComplete={refetchStripeStatus}
        />
    </PageContainer>
  );
}

export default SettingAdmin;
